/**
 * Контурные значения, которые backend кладёт на window
 * (см. app/views/shared/_region_globals.html.slim).
 *
 * RU: RUB / ₽, KZ: KZT / ₸. Чтение через хелпер, а не window в шаблоне:
 * шаблон Vue 3 не должен ходить в глобальный объект напрямую.
 */

declare global {
  interface Window {
    region?: string;
    currencyCode?: string;
    currencySymbol?: string;
    vatText?: string;
    adminUrl?: string;
    invoiceConsumerText?: string;
  }
}

type RegionWindowKey =
  | 'region'
  | 'currencyCode'
  | 'currencySymbol'
  | 'vatText'
  | 'adminUrl'
  | 'invoiceConsumerText';

// Контур сайта: backend кладёт REGION ('ru' или 'kz' — см. config/initializers/00_region.rb).
// Фолбэк — 'ru': если разметка почему-то не выставила window.region, фронт должен
// вести себя как раньше, а не переключаться на казахстанские реквизиты.
const DEFAULT_REGION = 'ru';
const DEFAULT_CURRENCY_CODE = 'RUB';
// Символ по коду валюты контура. Повторяет CURRENCY_SYMBOLS из
// api/config/initializers/00_region.rb: если контур задал только CURRENCY_CODE,
// символ всё равно должен быть его валютой, а не рублём по умолчанию
// (CURRENCY_SYMBOL из окружения по-прежнему главнее — см. getCurrencySymbol).
const CURRENCY_SYMBOLS: Record<string, string> = {
  RUB: '₽',
  KZT: '₸',
  USD: '$',
  EUR: '€',
  CNY: '¥'
};
const DEFAULT_VAT_TEXT = 'НДС 22%';
const DEFAULT_ADMIN_URL = 'https://is.scanros.com';
// Запасные реквизиты покупателя для документов — российские (ООО «СКАНРОСТ» с ИНН и КПП).
// В казахстанском контуре их подставлять нельзя: в счёте оказались бы чужие реквизиты,
// а не ТОО «Сканрос». Там текст приходит с бэкенда (REQUISITES_* → INVOICE_CONSUMER_TEXT),
// и если его почему-то нет, лучше оставить строку пустой, чем показать российские данные.
const DEFAULT_INVOICE_CONSUMER_TEXT =
  'ООО «СКАНРОСТ», ИНН/КПП  6164122993 / 616401001, 344082, Ростовская область, г. Ростов-на-Дону, ул. Береговая, д.8 оф. 1010';
const DEFAULT_INVOICE_CONSUMER_TEXT_KZ = '';

function readWindow(key: RegionWindowKey, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const value = window[key];
  return typeof value === 'string' && value ? value : fallback;
}

/** Код контура в нижнем регистре: 'ru' или 'kz'. */
export function getRegion(): string {
  return readWindow('region', DEFAULT_REGION).toLowerCase();
}

/** Казахстанский контур: другие реквизиты (БИН/ИИК), валюта и интеграции. */
export function isKzRegion(): boolean {
  return getRegion() === 'kz';
}

export function getCurrencyCode(): string {
  return readWindow('currencyCode', DEFAULT_CURRENCY_CODE);
}

export function getCurrencySymbol(): string {
  const code = getCurrencyCode();

  return readWindow('currencySymbol', CURRENCY_SYMBOLS[code] ?? code);
}

export function getVatText(): string {
  return readWindow('vatText', DEFAULT_VAT_TEXT);
}

export function getAdminUrl(): string {
  return readWindow('adminUrl', DEFAULT_ADMIN_URL);
}

export function getInvoiceConsumerText(): string {
  const fallback = getRegion() === 'kz' ? DEFAULT_INVOICE_CONSUMER_TEXT_KZ : DEFAULT_INVOICE_CONSUMER_TEXT;

  return readWindow('invoiceConsumerText', fallback);
}

export {};
